import RadioButton from "@components/inputs/RadioButton";
import SlidingPanel from "@components/sliders/SlidingPanel";
import Switcher from "@components/switcher/Switcher";
import { format } from "date-fns";
import { useState } from "react";
import { DateRange } from "react-date-range";

interface FilterProp {
  startDate?: string;
  endDate?: string;
  ledger?: string;
}

interface PeriodProps {
  startDate?: string;
  endDate?: string;
  selectedPeriod?: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onPeriodSelect: (period: string) => void;
}

interface LedgerProps {
  ledger?: string;
  onLedgerChange: (ledger: string) => void;
}

const defaultFilters: FilterProp = {
  startDate: "",
  endDate: "",
  ledger: "",
};

const TransactionFilter = ({
  isOpen,
  onClose,
  onApply,
}: {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterProp) => void;
}) => {
  const [tempFilters, setTempFilters] = useState<
    FilterProp & { selectedPeriod?: string }
  >({
    ...defaultFilters,
    selectedPeriod: "",
  });

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const tabs = [
    {
      label: "PERIOD",
      component: (
        <Period
          startDate={tempFilters.startDate}
          endDate={tempFilters.endDate}
          selectedPeriod={tempFilters.selectedPeriod}
          onStartDateChange={(date) =>
            setTempFilters((prev) => ({ ...prev, startDate: date }))
          }
          onEndDateChange={(date) =>
            setTempFilters((prev) => ({ ...prev, endDate: date }))
          }
          onPeriodSelect={(period) =>
            setTempFilters((prev) => ({ ...prev, selectedPeriod: period }))
          }
        />
      ),
    },
    {
      label: "LEDGER",
      component: (
        <Ledger
          ledger={tempFilters.ledger}
          onLedgerChange={(ledger) =>
            setTempFilters((prev) => ({ ...prev, ledger }))
          }
        />
      ),
    },
  ];

  return (
    <SlidingPanel
      heading="Filter Transactions"
      isOpen={isOpen}
      showConfirm={false}
      onClose={onClose}
      isSlider={false}>
      <div className="flex flex-col p-[16px] gap-[16px] min-h-[40%]">
        <Switcher tabs={tabs} />
        <button
          className="bg-bg-button-primary text-white text-[12px] py-[8px] rounded-lg"
          onClick={handleApply}>
          Apply Filters
        </button>
      </div>
    </SlidingPanel>
  );
};

const Period = ({
  onStartDateChange,
  onEndDateChange,
  startDate,
  endDate,
  selectedPeriod,
  onPeriodSelect,
}: PeriodProps) => {
  const [showCustomDateSelector, setShowCustomDateSelector] = useState(
    selectedPeriod === "custom",
  );

  const [range, setRange] = useState([
    {
      startDate: startDate ? new Date(startDate) : new Date(),
      endDate: endDate ? new Date(endDate) : new Date(),
      key: "selection",
    },
  ]);

  const sendDates = (start: Date, end: Date) => {
    const formattedStart = format(start, "yyyy-MM-dd HH:mm:ss");
    const formattedEnd = format(end, "yyyy-MM-dd HH:mm:ss");

    onStartDateChange(formattedStart);
    onEndDateChange(formattedEnd);
  };

  const handleDateRangeChange = (ranges: any) => {
    const selection = ranges.selection;

    if (selection.startDate && selection.endDate) {
      const start = new Date(selection.startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(selection.endDate);
      end.setDate(end.getDate() + 1);
      end.setHours(0, 0, 0, 0);

      sendDates(start, end);

      onPeriodSelect("custom");
    }

    setRange([selection]);
  };

  return (
    <div className="flex flex-col h-[60dvh] pt-[16px]">
      <div className="flex flex-row gap-[12px] justify-center">
        <div
          className={`border rounded-full px-[16px] py-[4px] cursor-pointer ${
            selectedPeriod === "today" ? "bg-bg-button-secondary" : ""
          }`}
          onClick={() => {
            onPeriodSelect("today");

            setShowCustomDateSelector(false);

            const today = new Date();

            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);

            const start = new Date(today);
            start.setHours(0, 0, 0, 0);

            const end = new Date(tomorrow);
            end.setHours(0, 0, 0, 0);

            sendDates(start, end);
          }}>
          <span className="text-[10px] font-semibold">Today</span>
        </div>

        <div
          className={`border rounded-full px-[16px] py-[4px] cursor-pointer ${
            selectedPeriod === "lastWeek" ? "bg-bg-button-secondary" : ""
          }`}
          onClick={() => {
            onPeriodSelect("lastWeek");

            setShowCustomDateSelector(false);

            const today = new Date();

            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);

            const start = new Date(weekAgo);
            start.setHours(0, 0, 0, 0);

            const end = new Date(today);
            end.setHours(0, 0, 0, 0);

            sendDates(start, end);
          }}>
          <span className="text-[10px] font-semibold">Last Week</span>
        </div>

        <div
          className={`border rounded-full px-[16px] py-[4px] cursor-pointer ${
            selectedPeriod === "custom" ? "bg-bg-button-secondary" : ""
          }`}
          onClick={() => {
            onPeriodSelect("custom");
            setShowCustomDateSelector(true);
          }}>
          <span className="text-[10px] font-semibold">Custom</span>
        </div>
      </div>

      {showCustomDateSelector && (
        <div className="mt-[16px] border rounded-lg">
          <DateRange
            ranges={range}
            onChange={handleDateRangeChange}
            moveRangeOnFirstSelection={false}
            editableDateInputs={false}
            showDateDisplay={false}
          />
        </div>
      )}
    </div>
  );
};

const Ledger = ({ ledger, onLedgerChange }: LedgerProps) => {
  return (
    <div className="flex flex-row gap-[16px] pt-[16px]">
      <RadioButton
        label="Credit"
        checked={ledger === "CR"}
        isEnabled={true}
        onChange={() => onLedgerChange(ledger === "CR" ? "" : "CR")}
      />

      <RadioButton
        label="Debit"
        checked={ledger === "DR"}
        isEnabled={true}
        onChange={() => onLedgerChange(ledger === "DR" ? "" : "DR")}
      />
    </div>
  );
};

export default TransactionFilter;
